import { ReactNode } from "react"

import type { JSONArray, JSONObject, JSONValue } from "@llumiverse/core"
import clsx from "clsx"

import { computeTitleFromName } from "@/lib/properties"

interface PropertiesPanelProps {
    value: JSONObject
    className?: string
}
export function PropertiesPanel({ value, className }: Readonly<PropertiesPanelProps>) {
    return (
        <div className={clsx("flex flex-col gap-4 px-4 pb-2", className)}>
            {
                Object.entries(value).map(([key, value]) =>
                    <PropertyElement key={key} name={key} value={value} />
                )
            }
        </div>
    )
}

interface PropertyTitleProps {
    name: string
}
function PropertyTitle({ name }: Readonly<PropertyTitleProps>) {
    return (
        <div className='text font-medium font-semibold break-all'>{computeTitleFromName(name)}</div>
    )
}

interface BlockElementProps {
    children: ReactNode | ReactNode[]
    className?: string
}
function BlockElement({ children, className }: Readonly<BlockElementProps>) {
    return (
        <div className={clsx("flex flex-col gap-4 py-0 pl-4 border-l-4 border-l-solid border-gray-400", className)}>
            {children}
        </div>
    )
}

interface PropertyElementProps {
    name: string
    value: JSONValue
}
function PropertyElement({ name, value }: Readonly<PropertyElementProps>) {
    const info = getValueInfo(value)
    switch (info.type) {
        case ValueType.Inline:
            return (
                <div className='w-full flex flex-col'>
                    <PropertyTitle name={name} />
                    <div className="text-sm">{info.value}</div>
                </div>
            )
        case ValueType.Paragraph:
            return (
                <div>
                    <PropertyTitle name={name} />
                    <div className="text-sm">{info.value}</div>
                </div>
            )
        case ValueType.Prose:
            return (
                <div className="prose">
                    <PropertyTitle name={name} />
                    <div className='text-sm prose'>{info.value}</div>
                </div>
            )
        case ValueType.Array:
            return (
                <ArrayProperty name={name} value={value as JSONArray} />
            )
        case ValueType.Object:
            return (
                <div>
                    <PropertyTitle name={name} />
                    <BlockElement>
                        {
                            Object.entries(value as JSONObject).map(([key, value]) => <PropertyElement key={key} name={key} value={value} />)
                        }
                    </BlockElement>
                </div>
            )
    }
}

interface ArrayPropertyProps {
    name?: string,
    value: JSONArray
}
function ArrayProperty({ name, value }: ArrayPropertyProps) {
    const isInline = (typeof value[0] === "string")
    const useBullet = true
    return (
        isInline
            ? (
                <div className='flex flex-col gap-2'>
                    {name && <PropertyTitle name={name} />}
                    <div className="flex flex-wrap gap-2">
                        {
                            value.map((item, index) =>
                                <div onClick={() => navigator.clipboard.writeText(String(item))} key={index} className='w-fit text-sm border border-gray-400 rounded-md p-1 gap-1 hover:bg-gray-200 hover:cursor-pointer'>
                                    {String(item)}
                                </div>
                            )
                        }
                    </div>
                </div>
            )
            : (
                <div>
                    {name && <PropertyTitle name={name} />}
                    <div className='flex flex-col gap-2'>
                        {
                            (value).map((value, index) => <ItemProperty key={crypto.randomUUID()} index={index} value={value} useBullet={useBullet} />)
                        }
                    </div>
                </div>
            )
    )
}

interface ItemPropertyProps {
    index: number
    value: JSONValue
    useBullet?: boolean
}
function ItemProperty({ index, value, useBullet }: Readonly<ItemPropertyProps>) {
    const bullet = useBullet ? <span className='text-sm'>•</span> : <span className='text-sm'>{index + 1}.</span>
    const info = getValueInfo(value)
    let content
    switch (info.type) {
        case ValueType.Object:
            content = <BlockElement>
                {
                    Object.entries(value as JSONObject).map(([key, value]) => <PropertyElement key={key} name={key} value={value} />)
                }
            </BlockElement>
            break
        case ValueType.Array:
            content = <ArrayProperty value={value as JSONArray} />
            break
        case ValueType.Prose: content = <div className="text-sm prose">{info.value}</div>
            break
        default: content = <div className="text-sm">{info.value}</div>
            break
    }
    return (
        <div className='flex gap-4 hover:bg-slate-50 py-2 pr-2 pl-4'>
            <div className='font-semibold text-gray-600'>{bullet}</div>
            <div className="text-sm muted-foreground"><span className="flex h-full items-center">{content}</span></div>
        </div>
    )
}

enum ValueType {
    Inline,
    Paragraph,
    Prose,
    Array,
    Object
}
function getValueInfo(value: JSONValue): { value: any, type: ValueType } {
    if (value == null) {
        return {
            value: "-",
            type: ValueType.Inline
        }
    }
    if (Array.isArray(value)) {
        return {
            value,
            type: ValueType.Array
        }
    }
    const type = typeof value
    if (type === "string") {
        const len = (value as string).length
        let type
        if (len < 80) {
            type = ValueType.Inline
        } else if (len > 400) {
            type = ValueType.Prose
        } else {
            type = ValueType.Paragraph
            value = (value as string).replace(/(?:\n\n)+/g, "\n\n")
        }
        return { type, value }
    } else if (type === "number" || type === "boolean") {
        return {
            value: String(value),
            type: ValueType.Inline
        }
    } else {
        return {
            value,
            type: ValueType.Object
        }
    }
}